import { z } from "zod";

import { getNormalEnum } from "../pseudoEnums.js";
import { zGuard } from "../zod/utils.js";

import { ToolNameLiteral } from "./agent/toolNames.js";
import { DEFAULT_COMPACTION_THRESHOLD_TOKENS } from "./compactionDefaults.js";
import { EffortLevel, ModelSpeedLiteral } from "./effortLevel.js";
import { ModelFeatures } from "./ModelFeatures.js";

export const AnthropicLikeProviderTypeLiteral = z.enum([
  "ANTHROPIC",
  "BEDROCK",
  /**
   * VERTEX = Gemini Enterprise Agent Platform (formerly Vertex AI).
   * This value gets persisted in MagicApiKey.providerName so would need a migration to updated
   */
  "VERTEX",
]);
export type AnthropicLikeProviderType = z.infer<
  typeof AnthropicLikeProviderTypeLiteral
>;
export const AnthropicLikeProviderType = getNormalEnum(
  AnthropicLikeProviderTypeLiteral,
);

export type AnthropicProviderFeatures = {
  compaction: boolean;
  toolSearch: "new" | "legacy";
  taskBudgets: boolean;
  /**
   * Whether the provider honors the `speed: "fast"` param.
   */
  fastMode: boolean;
  /** Whether the provider accepts `strict: true` on tool definitions. */
  strictToolUse: boolean;
};

export const AnthropicProviderFeatureMap: Record<
  AnthropicLikeProviderType,
  AnthropicProviderFeatures
> = {
  [AnthropicLikeProviderType.ANTHROPIC]: {
    compaction: true,
    toolSearch: "new",
    taskBudgets: true,
    fastMode: true,
    strictToolUse: true,
  },
  [AnthropicLikeProviderType.BEDROCK]: {
    // Bedrock supports server-side compaction via the InvokeModel API (which is
    // what @anthropic-ai/bedrock-sdk uses), using the same compact-2026-01-12
    // beta header + context_management envelope as the direct Anthropic API.
    compaction: true,
    toolSearch: "legacy",
    taskBudgets: false,
    fastMode: false,
    strictToolUse: false,
  },
  [AnthropicLikeProviderType.VERTEX]: {
    // Vertex's Anthropic beta rollout is inconsistent per model/region (the
    // Haiku tier in particular lags), so leave compaction off until it can be
    // validated against the exact models/regions we route to.
    compaction: false,
    toolSearch: "legacy",
    taskBudgets: false,
    fastMode: false,
    strictToolUse: false,
  },
};

/** @deprecated */
export const DeprecatedAnthropicModelLiteral = z.enum([
  "CLAUDE_4_5_SONNET",
  "CLAUDE_4_6_SONNET",
  "CLAUDE_4_7_OPUS",
  "CLAUDE_4_SONNET",
  "CLAUDE_SONNET_35",
  "CLAUDE_SONNET_35_NEW",
  "CLAUDE_SONNET_37",
  "CLAUDE_3_OPUS",
  "RESEARCH_MODEL_FIREFLY",
  "CLAUDE_4_OPUS",
  "CLAUDE_4_5_OPUS",
  "CLAUDE_NOUGAT_EAP",
  "CLAUDE_FUDGE_EAP",
  "CLAUDE_CHURRO_EAP",
]);
/** @deprecated */
export type DeprecatedAnthropicModel = z.infer<
  typeof DeprecatedAnthropicModelLiteral
>;
/** @deprecated */
export const DeprecatedAnthropicModel = getNormalEnum(
  DeprecatedAnthropicModelLiteral,
);

export const StableAnthropicModelLiteral = z.enum([
  "CLAUDE_4_5_HAIKU",
  "CLAUDE_5_5_HAIKU",
  "CLAUDE_4_6_OPUS",
  "CLAUDE_4_8_OPUS",
  "CLAUDE_OPUS_5",
  "CLAUDE_OPUS_5_5",
  "CLAUDE_5_SONNET",
  "CLAUDE_5_5_SONNET",
]);
export type StableAnthropicModel = z.infer<typeof StableAnthropicModelLiteral>;
export const StableAnthropicModel = getNormalEnum(StableAnthropicModelLiteral);

export const ExperimentalAnthropicModelLiteral = z.enum([
  "CLAUDE_FABLE_5",
  "CLAUDE_FABLE_5_1",
]);
export type ExperimentalAnthropicModel = z.infer<
  typeof ExperimentalAnthropicModelLiteral
>;
export const ExperimentalAnthropicModel = getNormalEnum(
  ExperimentalAnthropicModelLiteral,
);

export const AnthropicModelLiteral = z.enum([
  ...StableAnthropicModelLiteral.options,
  ...ExperimentalAnthropicModelLiteral.options,
]);
export type AnthropicModel = z.infer<typeof AnthropicModelLiteral>;
export const AnthropicModel = getNormalEnum(AnthropicModelLiteral);

export const GeminiEAPMultiRegionLiteral = z.enum(["US", "EU"]);
export type GeminiEAPMultiRegion = z.infer<typeof GeminiEAPMultiRegionLiteral>;
export const GeminiEAPMultiRegion = getNormalEnum(GeminiEAPMultiRegionLiteral);

export const GeminiEAPRegionLiteral = z
  .string()
  .refine(
    (region) =>
      region.length <= 63 && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(region),
    {
      message:
        "Vertex region must contain only lowercase letters, numbers, and hyphens",
    },
  );
export type GeminiEAPRegion = z.infer<typeof GeminiEAPRegionLiteral>;

export type AnthropicModelFeatures = ModelFeatures & {
  /**
   * Should omit geo prefixes
   * @see {@link https://docs.aws.amazon.com/bedrock/latest/userguide/model-cards.html}
   */
  bedrockName: string;
  /**
   * @see {@link https://docs.cloud.google.com/vertex-ai/generative-ai/docs/partner-models/claude}
   */
  geminiEAPName: string;
  /**
    * For each possible psuedo region, the actual region the model is supported in

    * @see {@link https://docs.cloud.google.com/gemini-enterprise-agent-platform/resources/locations}
    */
  geminiEAPRegion: Record<GeminiEAPMultiRegion, string>;
  oneMillionContext?: true;
  adaptiveThinking?: true;
  requiresAdaptiveThinking?: true;
  disallowsForcedToolUse?: true;
  summarizedAdaptiveThinking?: true;
  omitsSamplingParams?: true;
  xHighEffort?: true;
  compaction?: true;
  effort?: true;
  serverSideFallbackModel?: AnthropicModel;
  /** Whether the model accepts task budgets in `output_config` via its beta header. */
  taskBudgets?: true;
  /** Whether the model supports `speed: "fast"` via the fast-mode beta header. */
  fastMode?: true;
  /** Opt-in: strict tool schemas are sent only to models validated with them. */
  strictToolUse?: true;
};

export const AnthropicModelFeatureMap: Record<
  AnthropicModel,
  AnthropicModelFeatures
> = {
  [AnthropicModel.CLAUDE_4_5_HAIKU]: {
    name: "claude-haiku-4-5-20251001",
    label: "Haiku 4.5",
    requiresDataRetention: false,
    bedrockName: "anthropic.claude-haiku-4-5-20251001-v1:0",
    geminiEAPName: "claude-haiku-4-5@20251001",
    geminiEAPRegion: {
      [GeminiEAPMultiRegion.EU]: "europe-west1",
      [GeminiEAPMultiRegion.US]: "us-east5",
    },
  },
  [AnthropicModel.CLAUDE_5_5_HAIKU]: {
    name: "claude-haiku-5-5",
    label: "Haiku 5.5",
    requiresDataRetention: false,
    bedrockName: "anthropic.claude-haiku-5-5",
    geminiEAPName: "claude-haiku-5-5",
    geminiEAPRegion: {
      [GeminiEAPMultiRegion.EU]: "eu",
      [GeminiEAPMultiRegion.US]: "us",
    },
    effort: true,
    adaptiveThinking: true,
    oneMillionContext: true,
    omitsSamplingParams: true,
    summarizedAdaptiveThinking: true,
    xHighEffort: true,
  },
  [AnthropicModel.CLAUDE_4_6_OPUS]: {
    name: "claude-opus-4-6",
    label: "Opus 4.6",
    requiresDataRetention: false,
    bedrockName: "anthropic.claude-opus-4-6-v1",
    geminiEAPName: "claude-opus-4-6",
    geminiEAPRegion: {
      [GeminiEAPMultiRegion.EU]: "europe-west1",
      [GeminiEAPMultiRegion.US]: "us-east5",
    },
    effort: true,
    compaction: true,
    adaptiveThinking: true,
    oneMillionContext: true,
  },
  [AnthropicModel.CLAUDE_4_8_OPUS]: {
    name: "claude-opus-4-8",
    label: "Opus 4.8",
    requiresDataRetention: false,
    bedrockName: "anthropic.claude-opus-4-8",
    geminiEAPName: "claude-opus-4-8",
    geminiEAPRegion: {
      [GeminiEAPMultiRegion.EU]: "eu",
      [GeminiEAPMultiRegion.US]: "us",
    },
    effort: true,
    compaction: true,
    adaptiveThinking: true,
    oneMillionContext: true,
    omitsSamplingParams: true,
    summarizedAdaptiveThinking: true,
    xHighEffort: true,
    taskBudgets: true,
    // Fast mode for this model is removed on 2026-11-30. After that, a request
    // with speed: "fast" returns 400. Opus 5 and Opus 5.5 still accept it.
  },
  [AnthropicModel.CLAUDE_OPUS_5]: {
    name: "claude-opus-5",
    label: "Opus 5",
    requiresDataRetention: false,
    bedrockName: "anthropic.claude-opus-5",
    geminiEAPName: "claude-opus-5",
    geminiEAPRegion: {
      [GeminiEAPMultiRegion.EU]: "eu",
      [GeminiEAPMultiRegion.US]: "us",
    },
    effort: true,
    compaction: true,
    adaptiveThinking: true,
    oneMillionContext: true,
    omitsSamplingParams: true,
    summarizedAdaptiveThinking: true,
    xHighEffort: true,
    taskBudgets: true,
    fastMode: true,
  },
  [AnthropicModel.CLAUDE_OPUS_5_5]: {
    name: "claude-opus-5-5",
    label: "Opus 5.5",
    requiresDataRetention: false,
    bedrockName: "anthropic.claude-opus-5-5",
    geminiEAPName: "claude-opus-5-5",
    geminiEAPRegion: {
      [GeminiEAPMultiRegion.EU]: "eu",
      [GeminiEAPMultiRegion.US]: "us",
    },
    effort: true,
    compaction: true,
    adaptiveThinking: true,
    requiresAdaptiveThinking: true,
    disallowsForcedToolUse: true,
    oneMillionContext: true,
    omitsSamplingParams: true,
    summarizedAdaptiveThinking: true,
    xHighEffort: true,
    taskBudgets: true,
    fastMode: true,
  },
  [AnthropicModel.CLAUDE_FABLE_5]: {
    name: "claude-fable-5",
    label: "Fable 5",
    requiresDataRetention: true,
    bedrockName: "anthropic.claude-fable-5",
    geminiEAPName: "claude-fable-5",
    geminiEAPRegion: {
      [GeminiEAPMultiRegion.EU]: "eu",
      [GeminiEAPMultiRegion.US]: "us",
    },
    serverSideFallbackModel: AnthropicModel.CLAUDE_4_8_OPUS,
    effort: true,
    compaction: true,
    adaptiveThinking: true,
    oneMillionContext: true,
    omitsSamplingParams: true,
    summarizedAdaptiveThinking: true,
    xHighEffort: true,
    taskBudgets: true,
  },
  [AnthropicModel.CLAUDE_FABLE_5_1]: {
    name: "claude-fable-5-1",
    label: "Fable 5.1",
    requiresDataRetention: true,
    bedrockName: "anthropic.claude-fable-5-1",
    geminiEAPName: "claude-fable-5-1",
    geminiEAPRegion: {
      [GeminiEAPMultiRegion.EU]: "eu",
      [GeminiEAPMultiRegion.US]: "us",
    },
    effort: true,
    compaction: true,
    adaptiveThinking: true,
    oneMillionContext: true,
    omitsSamplingParams: true,
    summarizedAdaptiveThinking: true,
    xHighEffort: true,
  },
  [AnthropicModel.CLAUDE_5_SONNET]: {
    name: "claude-sonnet-5",
    label: "Sonnet 5",
    requiresDataRetention: false,
    bedrockName: "anthropic.claude-sonnet-5",
    geminiEAPName: "claude-sonnet-5",
    geminiEAPRegion: {
      [GeminiEAPMultiRegion.EU]: "eu",
      [GeminiEAPMultiRegion.US]: "us",
    },
    effort: true,
    compaction: true,
    adaptiveThinking: true,
    oneMillionContext: true,
    omitsSamplingParams: true,
    summarizedAdaptiveThinking: true,
    xHighEffort: true,
    strictToolUse: true,
  },
  [AnthropicModel.CLAUDE_5_5_SONNET]: {
    name: "claude-sonnet-5-5",
    label: "Sonnet 5.5",
    requiresDataRetention: false,
    bedrockName: "anthropic.claude-sonnet-5-5",
    geminiEAPName: "claude-sonnet-5-5",
    geminiEAPRegion: {
      [GeminiEAPMultiRegion.EU]: "eu",
      [GeminiEAPMultiRegion.US]: "us",
    },
    effort: true,
    compaction: true,
    adaptiveThinking: true,
    // Sonnet 5.5 rejects thinking.type "disabled" and forced tool_choice with a 400.
    requiresAdaptiveThinking: true,
    disallowsForcedToolUse: true,
    oneMillionContext: true,
    omitsSamplingParams: true,
    summarizedAdaptiveThinking: true,
    xHighEffort: true,
  },
};

/**
 * Resolves the Bedrock model ID for a region by adding a cross-region
 * inference-profile geo prefix so the model works regardless of the specific
 * region the credentials were created in. Falls back to `global` for
 * non-us/eu regions.
 * @see {@link https://docs.aws.amazon.com/bedrock/latest/userguide/models-region-compatibility.html}
 */
export function getBedrockModelName({
  awsRegion,
  model,
}: {
  model: AnthropicModel;
  awsRegion: string;
}): string {
  const baseName = AnthropicModelFeatureMap[model].bedrockName;
  const prefix = awsRegion.startsWith("us-")
    ? "us"
    : awsRegion.startsWith("eu-")
      ? "eu"
      : "global";
  return `${prefix}.${baseName}`;
}

/**
 * Resolves the Gemini EAP region to use for a model. When a `multiRegion` is
 * provided, returns the model's multi-region endpoint from
 * `AnthropicModelFeatureMap`. Otherwise, maps a specific `region` to the
 * corresponding multi-region endpoint (`europe-*` -> EU, `us-*` -> US), falling
 * back to the region as-is for anything else.
 * @see {@link https://docs.cloud.google.com/gemini-enterprise-agent-platform/resources/locations}
 */
export function getGeminiEAPRegion({
  model,
  multiRegion,
  region,
}: {
  model: AnthropicModel;
  region?: string;
  multiRegion?: GeminiEAPMultiRegion;
}): string {
  if (multiRegion != null) {
    return AnthropicModelFeatureMap[model].geminiEAPRegion[multiRegion];
  } else if (region != null) {
    const regionResult = GeminiEAPRegionLiteral.safeParse(region);
    if (!regionResult.success) {
      // the schema's own message, not `formatZodErrorForLogging`: this is an
      // operator-facing error and the refine messages here are static strings,
      // where the log formatter deliberately drops `message` because it can
      // interpolate the received value
      throw new Error(
        regionResult.error.issues.map((issue) => issue.message).join("; "),
      );
    }
    if (region.startsWith("europe-")) {
      return getGeminiEAPRegion({
        model,
        multiRegion: GeminiEAPMultiRegion.EU,
      });
    } else if (region.startsWith("us-")) {
      return getGeminiEAPRegion({
        model,
        multiRegion: GeminiEAPMultiRegion.US,
      });
    } else {
      return region;
    }
  } else {
    throw new Error("Cannot resolve gemini EAP region");
  }
}

export const AnthropicToolChoiceLiteral = z.enum([
  // z.literal("tool"), // TODO(@zkirby): Support "tool" tool choice option.
  "any",
  "auto",
  "none",
]);
export type AnthropicToolChoice = z.infer<typeof AnthropicToolChoiceLiteral>;

export const DEFAULT_THINKING_BUDGET_TOKENS = 5000;
export const DEFAULT_MAX_TOKENS = 128_000;

const CacheControl = z.object({
  type: z.literal("ephemeral"),
});

export const AnthropicThinkingBlock = z.object({
  type: z.literal("thinking"),
  thinking: z.string(),
  signature: z.string(),
  cache_control: CacheControl.optional(),
});
export type AnthropicThinkingBlock = z.infer<typeof AnthropicThinkingBlock>;

export const RedactedAnthropicThinkingBlock = z.object({
  type: z.literal("redacted_thinking"),
  data: z.string(), // field is 'data' not 'thinking' for redacted blocks
  signature: z.string(),
  cache_control: CacheControl.optional(),
});
export type RedactedAnthropicThinkingBlock = z.infer<
  typeof RedactedAnthropicThinkingBlock
>;

export const AnthropicThinkingBlocks = z.array(
  z.union([AnthropicThinkingBlock, RedactedAnthropicThinkingBlock]),
);
export type AnthropicThinkingBlocks = z.infer<typeof AnthropicThinkingBlocks>;

export const AnthropicThinkingConfig = z.object({
  enabled: z.boolean(),
  budgetTokens: z.number(),
});
export type AnthropicThinkingConfig = z.infer<typeof AnthropicThinkingConfig>;

export const AnthropicTaskBudget = z.object({
  type: z.literal("tokens"),
  total: z.number(),
  remaining: z.number().optional(),
});
export type AnthropicTaskBudget = z.infer<typeof AnthropicTaskBudget>;

export const AnthropicEffortLiteral = z.enum([
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
]);
export type AnthropicEffort = z.infer<typeof AnthropicEffortLiteral>;

/**
 * Canonical {@link EffortLevel} → Anthropic API param. `none` and `minimal`
 * collapse to `low`; per-model clamping (e.g. xhigh→high) happens downstream
 * in `buildEffortForRequest`.
 */
export function toAnthropic(p: EffortLevel): AnthropicEffort {
  if (p === "none" || p === "minimal") return "low";
  return p;
}

/**
 * Config for Anthropic's tool search feature (beta header: advanced-tool-use-20
 * Each agent defines its own always-loaded tools in *_DEFAULT_ALWAYS_LOADED_TOOL_NAMES.
 * If tool search is enabled, other tools are deferred and loaded on demand.
 */
export const AnthropicToolSearchConfig = z.object({
  enabled: z.boolean(),
  /** Tool names that should never be deferred (always loaded into context) */
  alwaysLoadedTools: z.array(ToolNameLiteral).optional(),
});
export type AnthropicToolSearchConfig = z.infer<
  typeof AnthropicToolSearchConfig
>;

/**
 * Shared guidance describing _what_ a compaction summary should contain,
 * independent of the output mechanism (raw text/tags vs. structured output).
 *
 * These instructions optimize for "resume immediately" continuity in long
 * agent loops while avoiding excess detail that can be re-fetched.
 */
export const COMPACTION_SUMMARY_GUIDANCE = `
You have written a partial transcript for the initial task above. Please write a summary of the transcript.

The purpose of this summary is continuity: a future run may not have access to the raw history, and must continue the same task using only your summary without repeating already completed work or forgetting both long term goals, immediate next steps, or currently in progress work.

Write down anything that would be helpful, including the state, next steps, learnings etc. including but not limited to:
- The user's current goal, constraints, preferences, and success criteria.
- What has been completed, what remains, and the exact current state.
- Key decisions, assumptions, tradeoffs, and rationale already established.
- Important failures, pitfalls, and "do not repeat" learnings.
- Any blockers or open questions that still need user input.
- Immediate and detailed spec-level next steps in priority order.
- If a plan or notes file was written during the task, or an earlier summary names one (for example \`working/plan.md\`), keep its path in Critical Reminders to re-read.

Keep it concise and action-oriented:
- Preserve stable, human-meaningful references (cell names/purposes, dataset/table names, feature names).
- Do not include ephemeral IDs (short cell refs, UUIDs, tool call IDs). These will be automatically re-fetched from the notebook and can be referenced here in context with plain english.
- Exception: keep the IDs of the projects and branches you are working in (for example the branchId you pass to tools) exactly as written. They are not re-fetched automatically.
- Do not dump large raw outputs or full code/SQL unless required for correctness, these can all be re-fetched from the notebook later ad hoc.

End with:
- "Next Steps" checklist (ordered by priority, concrete actions).
- "Critical Reminders" (short bullets of must-not-forget constraints/pitfalls).
`.trim();

/**
 * Default instructions for Anthropic server-side compaction summaries.
 *
 * Used by the native Anthropic context-management compaction feature, which
 * returns the summary as free text wrapped in a <summary> block.
 */
export const DEFAULT_ANTHROPIC_COMPACTION_INSTRUCTIONS = `${COMPACTION_SUMMARY_GUIDANCE}

You must wrap your summary in a <summary></summary> block and output nothing else.`;

/**
 * Config for Anthropic server-side compaction context management edits.
 */
export const AnthropicCompactionConfig = z.object({
  enabled: z.boolean(),
  trigger: z
    .object({
      type: z.literal("input_tokens"),
      value: z.number(),
    })
    .optional(),
  pauseAfterCompaction: z.boolean().optional(),
  instructions: z.string().optional(),
});
export type AnthropicCompactionConfig = z.infer<
  typeof AnthropicCompactionConfig
>;
export const AnthropicDefaultCompactionConfig: AnthropicCompactionConfig = {
  enabled: true,
  pauseAfterCompaction: false,
  instructions: DEFAULT_ANTHROPIC_COMPACTION_INSTRUCTIONS,
  trigger: {
    type: "input_tokens",
    value: DEFAULT_COMPACTION_THRESHOLD_TOKENS,
  },
};

export const AnthropicModelParams = z.object({
  model: AnthropicModelLiteral,
  maxTokens: z.number(),
  stopSequences: z.array(z.string()),
  temperature: z.number().optional(),
  topK: z.number().optional(),
  topP: z.number().optional(),
  thinking: AnthropicThinkingConfig.optional(),
  effort: AnthropicEffortLiteral.optional(),
  speed: ModelSpeedLiteral.optional(),
  taskBudget: AnthropicTaskBudget.optional(),
  toolSearch: AnthropicToolSearchConfig.optional(),
});
export type AnthropicModelParams = z.infer<typeof AnthropicModelParams>;

export const AnthropicMessageRoleLiteral = z.enum(["user", "assistant"]);
export type AnthropicMessageRole = z.infer<typeof AnthropicMessageRoleLiteral>;
export const AnthropicMessageRole = getNormalEnum(AnthropicMessageRoleLiteral);

export const TextBlock = z.object({
  type: z.literal("text"),
  text: z.string(),
  cache_control: CacheControl.optional(),
});
export type TextBlock = z.infer<typeof TextBlock>;

export const AnthropicMediaType = z.enum([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
]);
type AnthropicMediaType = z.infer<typeof AnthropicMediaType>;

const ImageBlock = z.object({
  type: z.literal("image"),
  source: z.object({
    type: z.literal("base64"),
    media_type: AnthropicMediaType,
    data: z.string(),
  }),
  cache_control: CacheControl.optional(),
});
export type ImageBlock = z.infer<typeof ImageBlock>;

const DocumentBlock = z.object({
  type: z.literal("document"),
  source: z.object({
    type: z.literal("base64"),
    media_type: z.literal("application/pdf"),
    data: z.string(),
  }),
  cache_control: CacheControl.optional(),
});
export type DocumentBlock = z.infer<typeof DocumentBlock>;

export const ToolUseBlock = z.object({
  type: z.literal("tool_use"),
  id: z.string(),
  name: z.string(),
  input: z.record(z.string(), z.unknown()),
  thinking: z.string().optional(),
  cache_control: CacheControl.optional(),
});
export type ToolUseBlock = z.infer<typeof ToolUseBlock>;

/**
 * Tool reference block for client-side tool search.
 * Used in tool_result content to reference deferred tools.
 *
 * @see https://docs.anthropic.com/en/docs/agents-and-tools/tool-use/tool-search-tool#custom-tool-search-implementation
 */
export const ToolReferenceBlock = z.object({
  type: z.literal("tool_reference"),
  tool_name: z.string(),
  cache_control: CacheControl.optional(),
});
export type ToolReferenceBlock = z.infer<typeof ToolReferenceBlock>;

export const ToolResultBlock = z.object({
  type: z.literal("tool_result"),
  tool_use_id: z.string(),
  content: z.array(z.union([TextBlock, ImageBlock, ToolReferenceBlock])),
  cache_control: CacheControl.optional(),
});
export type ToolResultBlock = z.infer<typeof ToolResultBlock>;

/**
 * Compaction content block returned by Anthropic server-side compaction.
 */
export const AnthropicCompactionBlock = z.object({
  type: z.literal("compaction"),
  content: z.string(),
  cache_control: CacheControl.optional(),
});
export type AnthropicCompactionBlock = z.infer<typeof AnthropicCompactionBlock>;

export function isAnthropicCompactionBlock(
  block: unknown,
): block is AnthropicCompactionBlock {
  return zGuard(AnthropicCompactionBlock, block);
}

/**
 * Streaming delta for Anthropic compaction content.
 */
export const AnthropicCompactionDelta = z.object({
  type: z.literal("compaction_delta"),
  content: z.string(),
});
export type AnthropicCompactionDelta = z.infer<typeof AnthropicCompactionDelta>;

export function isAnthropicCompactionDelta(
  delta: unknown,
): delta is AnthropicCompactionDelta {
  return zGuard(AnthropicCompactionDelta, delta);
}

export const AnthropicCompactionEdit = z.object({
  type: z.literal("compact_20260112"),
  trigger: z
    .object({
      type: z.literal("input_tokens"),
      value: z.number(),
    })
    .optional(),
  pause_after_compaction: z.boolean().optional(),
  instructions: z.string().optional(),
});
export type AnthropicCompactionEdit = z.infer<typeof AnthropicCompactionEdit>;

export const AnthropicContextManagementWithCompaction = z.object({
  edits: z.array(AnthropicCompactionEdit),
});
export type AnthropicContextManagementWithCompaction = z.infer<
  typeof AnthropicContextManagementWithCompaction
>;

export const AnthropicMessage = z.object({
  role: AnthropicMessageRoleLiteral,
  content: z.array(
    z.union([
      TextBlock,
      ToolUseBlock,
      ToolResultBlock,
      AnthropicCompactionBlock,
      ImageBlock,
      DocumentBlock,
      AnthropicThinkingBlock,
      RedactedAnthropicThinkingBlock,
    ]),
  ),
});
export type AnthropicMessage = z.infer<typeof AnthropicMessage>;

export const AnthropicMessages = z.object({
  systemMessage: z.string(),
  userAndAssistantMessages: z.array(AnthropicMessage),
});
export type AnthropicMessages = z.infer<typeof AnthropicMessages>;

export const AnthropicConformerResult = z.object({
  messages: AnthropicMessages,
  totalContextTokens: z.number().or(z.null()),
  usedTokenCountApproximation: z.boolean(),
});
export type AnthropicConformerResult = z.infer<typeof AnthropicConformerResult>;

export const AnthropicErrorPayload = z.object({
  type: z.literal("error"),
  error: z.object({
    type: z.string(),
    message: z.string(),
  }),
});
export type AnthropicErrorPayload = z.infer<typeof AnthropicErrorPayload>;
